import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Users, Loader2, CheckCircle, XCircle, Clock, UserCheck, ShieldAlert, Home } from "lucide-react";

const statusColors = { PENDING: "bg-amber-100 text-amber-800", APPROVED: "bg-green-100 text-green-800", REJECTED: "bg-red-100 text-red-800" };

export default function CondoResidents() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [actionDialog, setActionDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetch = async () => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      const res = await api.get("/condo/memberships", { params });
      setMemberships(res.data);
    } catch { setMemberships([]); }
    setLoading(false);
  };

  useEffect(() => { setLoading(true); fetch(); }, [filter]); // eslint-disable-line

  const handleVerify = async () => {
    if (!actionDialog) return;
    setSaving(true);
    try {
      await api.put(`/condo/memberships/${actionDialog.id}/verify`, { action: actionDialog.action });
      toast.success(`Membership ${actionDialog.action.toLowerCase()}`);
      setActionDialog(null);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  const pending = memberships.filter(m => m.verification_status === "PENDING");
  const approved = memberships.filter(m => m.verification_status === "APPROVED");

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="condo-residents-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3">
            <Users className="w-8 h-8 text-court-orange" />Residents
          </h1>
          <p className="text-muted-foreground mt-1">Verify and manage resident memberships</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="resident-filter" className="w-48 h-11 rounded-none border-2">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ({memberships.length})</SelectItem>
            <SelectItem value="PENDING">Pending ({pending.length})</SelectItem>
            <SelectItem value="APPROVED">Approved ({approved.length})</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Pending Requests Alert */}
      {pending.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 p-4 mb-6 flex items-center gap-3" data-testid="pending-alert">
          <Clock className="w-5 h-5 text-amber-500 shrink-0" />
          <p className="text-sm font-medium text-amber-800">{pending.length} resident{pending.length !== 1 ? 's' : ''} awaiting verification</p>
        </div>
      )}

      {memberships.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200">
          <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No membership requests</p>
        </div>
      ) : (
        <div className="bg-white border-2 border-slate-100 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-heading uppercase tracking-wider text-xs">Resident</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Unit</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Status</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Requested</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {memberships.map(m => (
                <TableRow key={m.membership_id} data-testid={`membership-${m.membership_id}`}>
                  <TableCell>
                    <p className="font-medium text-sm">{m.user_name}</p>
                    <p className="text-[10px] text-muted-foreground">{m.user_email}</p>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 text-sm font-medium"><Home className="w-3 h-3 text-muted-foreground" />{m.unit_ref}</span>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${statusColors[m.verification_status]} text-[10px] font-heading uppercase`}>{m.verification_status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{m.created_at?.slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1.5 justify-end">
                      {m.verification_status === "PENDING" && (
                        <>
                          <Button data-testid={`approve-${m.membership_id}`} size="sm"
                            onClick={() => setActionDialog({ id: m.membership_id, action: "APPROVED", name: m.user_name })}
                            className="h-7 bg-green-600 hover:bg-green-700 text-white rounded-full font-heading uppercase text-[10px] px-3">
                            <CheckCircle className="w-3 h-3 mr-1" />Approve
                          </Button>
                          <Button data-testid={`reject-${m.membership_id}`} size="sm" variant="outline"
                            onClick={() => setActionDialog({ id: m.membership_id, action: "REJECTED", name: m.user_name })}
                            className="h-7 rounded-full border-2 border-red-200 text-red-500 hover:bg-red-50 font-heading uppercase text-[10px] px-3">
                            <XCircle className="w-3 h-3 mr-1" />Reject
                          </Button>
                        </>
                      )}
                      {m.verification_status === "APPROVED" && (
                        <Badge variant="outline" className="text-[10px] text-green-600 border-green-200"><UserCheck className="w-3 h-3 mr-1" />Verified</Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="verify-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-court-orange" />{actionDialog?.action === "APPROVED" ? "Approve" : "Reject"} Resident
            </DialogTitle>
            <DialogDescription>{actionDialog?.action === "APPROVED" ? "Approve" : "Reject"} membership for <strong>{actionDialog?.name}</strong>?</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setActionDialog(null)} className="flex-1 rounded-full border-2">Cancel</Button>
            <Button data-testid="confirm-verify-btn" onClick={handleVerify} disabled={saving}
              className={`flex-1 rounded-full font-heading uppercase ${actionDialog?.action === "APPROVED" ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}`}>
              {saving ? "Processing..." : actionDialog?.action === "APPROVED" ? "Approve" : "Reject"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
